import { useCallback, useRef, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client/react";
import { useAuth } from "../context/AuthContext";
import { useClient } from "../context/ClientContext";
import { SWITCH_COMPANY_MUTATION } from "../graphql/mutations";
import { COMPANY_QUERY } from "../graphql/queries";
import { showToast } from "../components/toastService";
import {
  authCorrelationHeaders,
  createRequestId,
  recordAuthPageAction,
  startTimer,
} from "../utils/perfTelemetry";

export type CompanySwitchTarget = {
  id: string;
  name: string;
};

export type CompanySwitchOptions = {
  /** After session is updated, before full page navigation */
  beforeReload?: () => void;
};

type SwitchContextUser = {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  companyId: string;
  homeCompanyId?: string;
  companyName?: string;
  schema?: string;
  onBoarding?: { status?: boolean } | null;
  aiEnabled?: boolean;
  twoFactorEnabled?: boolean;
};

type SwitchPayload = {
  switchCompanyContext: {
    accessToken: string;
    refreshToken?: string;
    user: SwitchContextUser;
  };
};

/**
 * Partner / multi-tenant flow: switch JWT + client context into a tenant company and hard-navigate.
 */
export function useCompanySwitch(): {
  switchTo: (company: CompanySwitchTarget, options?: CompanySwitchOptions) => Promise<void>;
  switching: boolean;
  switchingCompanyId: string | null;
} {
  const { login } = useAuth();
  const { setClientFromLogin } = useClient();
  const [switchCompanyMutation] = useMutation(SWITCH_COMPANY_MUTATION);
  const [fetchCompany] = useLazyQuery(COMPANY_QUERY);
  const [switchingCompanyId, setSwitchingCompanyId] = useState<string | null>(null);
  const inFlightRef = useRef(false);

  const switchTo = useCallback(
    async (company: CompanySwitchTarget, options?: CompanySwitchOptions) => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      setSwitchingCompanyId(company.id);
      const requestId = createRequestId();
      const elapsed = startTimer();
      try {
        const switchResult = await switchCompanyMutation({
          variables: { input: { companyId: company.id } },
          context: { headers: authCorrelationHeaders(requestId) },
        });

        const payload = switchResult.data as SwitchPayload | undefined;
        const ctx = payload?.switchCompanyContext;
        if (!ctx?.accessToken || !ctx.user) {
          throw new Error("Invalid switch company response");
        }

        localStorage.setItem("accessToken", ctx.accessToken);
        if (ctx.refreshToken) {
          localStorage.setItem("refreshToken", ctx.refreshToken);
        }

        try {
          const companyResult = await fetchCompany({
            variables: { id: company.id },
            context: { headers: authCorrelationHeaders(requestId) },
          });
          const co = (
            companyResult.data as {
              company?: { id: string; name: string; email?: string; phoneNumber?: string };
            }
          )?.company;
          if (co) {
            setClientFromLogin(co.id, co.name, co.email, co.phoneNumber);
          } else {
            setClientFromLogin(ctx.user.companyId, ctx.user.companyName || company.name, ctx.user.email);
          }
        } catch {
          setClientFromLogin(ctx.user.companyId, ctx.user.companyName || company.name, ctx.user.email);
        }

        login(ctx.user as any);
        options?.beforeReload?.();

        recordAuthPageAction({
          action: "switch_company_context",
          outcome: "success",
          duration_ms: elapsed(),
          requestId,
          role: ctx.user.role,
          status: 200,
        });

        const onboarded = Boolean((ctx.user as any).onBoarding?.status);
        window.location.assign(onboarded ? "/dashboard" : "/onboardingsessions");
      } catch (e: any) {
        const msg =
          e?.graphQLErrors?.[0]?.message || e?.message || "Could not switch company.";
        recordAuthPageAction({
          action: "switch_company_context",
          outcome: "failure",
          duration_ms: elapsed(),
          requestId,
          failure_stage: "client",
          status: 0,
        });
        showToast({ status: "error", title: msg });
        inFlightRef.current = false;
        setSwitchingCompanyId(null);
      }
    },
    [switchCompanyMutation, fetchCompany, setClientFromLogin, login]
  );

  return {
    switchTo,
    switching: Boolean(switchingCompanyId),
    switchingCompanyId,
  };
}
